import { forwardRef } from 'react';

import { Button as BaseButton } from '@base-ui/react/button';
import { cn } from 'components/utils';

import { buttonRecipe } from './Button.recipe';
import { ButtonContent } from './ButtonContent';

import type { Props } from './types';

/**
 * Design System 2.0 button.
 *
 * Ported from the Figma button matrix (file 5aSkE0U4TeWbuIZDvccHjc, node 8:279). Props mirror the
 * Figma variant axes through `appearance`, `fill` and `size`. Labelled Button sizes are exactly
 * `2xs`, `xs`, `regular` and `large`; the icon-only `3xs` belongs to IconButton.
 *
 * For a borderless text action, use `<Button appearance="tertiary" fill="default">Action</Button>`.
 * `fill="default"` may be omitted. Primary, Success and Destructive also support `fill="muted"`.
 * `isSelected` keeps a Muted button's active tint and adds the screen-reader pressed state.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor via the token layer in
 * `components/styles/design-system.css`, so the same markup is correct in both themes.
 *
 * Built on Base UI's `Button`, which supplies button semantics and keyboard activation even when
 * `render` swaps the element. Per Base UI's guidance, do NOT use this for navigation — reach for
 * `ButtonLink`, which is the same recipe on a real anchor.
 */
export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      appearance,
      fill,
      size,
      iconOnly,
      fullWidth,
      loading,
      isSelected,
      disabled,
      startIcon,
      endIcon,
      className,
      children,
      ...rest
    },
    ref
  ) => (
    <BaseButton
      ref={ref}
      // `focusableWhenDisabled` keeps a busy button in the tab order: focus should not jump
      // somewhere else the instant a submit starts. Base UI then uses `aria-disabled` rather than
      // the native attribute and blocks activation itself, so a second click cannot double-submit.
      disabled={disabled || loading}
      focusableWhenDisabled={loading || undefined}
      aria-busy={loading || undefined}
      className={cn(buttonRecipe({ appearance, fill, size, iconOnly, fullWidth, loading, isSelected }), className)}
      {...rest}
      aria-pressed={isSelected ?? rest['aria-pressed']}
    >
      <ButtonContent endIcon={endIcon} iconOnly={iconOnly} loading={loading} startIcon={startIcon}>
        {children}
      </ButtonContent>
    </BaseButton>
  )
);
